import { useMemo, useState } from "react";
import GuideButton from "@/components/UI/GuideButton";
import ProvenanceButton from "@/components/Provenance/ProvenanceButton";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import {
  ColumnVisibility,
  ColumnsType,
  TableState,
} from "@/types/tableState.types";
import FilterButton from "./Filtering/FilterButton";
import { ColumnButton } from "./ColumnsMenu/ColumnButton";
import PlotButton from "./PlotsMenu";
import DownloadButton from "./DownloadMenu";
import { SaveViewButton } from "./SaveViewButton/SaveViewButton";
import { useTableDictionary } from "@/utils/tableDictionaries/useTableDictionary";
import { useSuperuserMode } from "@/contexts/useSuperuser";
import { WorkspaceSection } from "./WorkspaceButton/WorkspaceSection";
import { WorkspaceButton } from "./WorkspaceButton/WorkspaceButton";

import isEqual from "lodash/isEqual";
import {
  workspaceConfigToTableState,
  createComparableWorkspaceState,
  comparableLayouts,
} from "@/utils/workspaceState";
import type { ToolbarButton } from "@/components/Templates/types";
import type { TableToolbarProps } from "./tableToolbar.types";
import type { VivoPlotProps } from "@/components/VivoPlots/types";

import SortingButton from "./Sorting/SortingButton";
import { isSingleSubjectPage } from "@/utils/workspaceUtils";

// Stable reference so the dashboard editor's `plots` effect does not reset
// in-progress edits when there are no plots.
const EMPTY_PLOTS: VivoPlotProps[] = [];

const TableToolbar = ({
  tableMetadata,
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
  maxMenuHeight,
  currentTableState,
  guideLink,
  datasetName,
  name,
  downloadName,
  fullPageName,
  tableStateFunctions,
  noFilteredDownload = false,
  viewSwitcher,
  endpointSuffix,
  endpointPrefix,
  workspace,
  activeTabToolbarConfig,
  downloadFilters,
  downloadFixedFilters,
  isDashboardEditorOpen = false,
  onToggleDashboardEditor,
  onCloseDashboardEditor,
  dashboardEditorProps,
}: TableToolbarProps) => {
  const { isSuperuserMode } = useSuperuserMode();
  const hasProvenance = useFeatureFlag("data-provenance");
  const [isWorkspaceDrawerOpen, setIsWorkspaceDrawerOpen] = useState(false);

  const shouldShow = (buttonName: ToolbarButton) => {
    if (!activeTabToolbarConfig?.showOnly) return true;
    return activeTabToolbarConfig.showOnly.includes(buttonName);
  };

  const isSSJ = isSingleSubjectPage(workspace?.tableName ?? "");

  // tableMetadata.columns is ColumnMetadata[] ({field, label}), but every
  // consumer below wants the field NAMES. Passing the objects straight through
  // silently breaks each one, because they test membership with
  // `metadataColumns.includes(column.fieldName)` — an object is never equal to a
  // field-name string. useCurrentWorkspace already maps to field names when it
  // builds initialTableState, so leaving it unmapped here made the two sides of
  // the dirty-state comparison disagree permanently.
  const metadataColumnFields = useMemo(
    () => tableMetadata?.columns?.map((column) => column.field),
    [tableMetadata?.columns],
  );

  const stateForComparison = (state: TableState) =>
    createComparableWorkspaceState(state, {
      excludeSubjectFilter: isSSJ,
      metadataColumns: metadataColumnFields,
      metadataFilterKeys: Object.keys(tableMetadata?.filters || {}),
    });

  const workspaceAsTableState = workspace?.currentWorkspace?.configuration
    ? workspaceConfigToTableState(
        workspace.currentWorkspace.configuration,
        metadataColumnFields,
        Object.keys(tableMetadata?.filters || {}),
      )
    : undefined;

  const currentComp = stateForComparison(currentTableState);
  const savedComp = stateForComparison(workspaceAsTableState);
  const hasPlotChanges = !isEqual(currentComp.plots, savedComp.plots);

  const isActiveColumn = (c: ColumnsType) =>
    c.visibility !== ColumnVisibility.NOT_AVAILABLE;
  const currentActiveColumns = (currentComp.columns as ColumnsType[]).filter(
    isActiveColumn,
  );
  const savedActiveColumns = (savedComp.columns as ColumnsType[]).filter(
    isActiveColumn,
  );

  const isDefault =
    isEqual(currentActiveColumns, savedActiveColumns) &&
    isEqual(currentComp.filters, savedComp.filters) &&
    isEqual(currentComp.sorting, savedComp.sorting) &&
    currentComp.lockedColumns === savedComp.lockedColumns &&
    !hasPlotChanges &&
    isEqual(
      comparableLayouts(currentTableState),
      comparableLayouts(workspaceAsTableState),
    );

  const { columnsTitleCreator } = useTableDictionary(datasetName);

  const liveEditedColumns = currentTableState?.columns || [];

  const visibleColumnNames = liveEditedColumns
    ? liveEditedColumns

        .filter((column: any) => column.visibility === ColumnVisibility.VISIBLE)

        .map((column: any) => column.fieldName)
    : [];

  const hiddenColumnNames = liveEditedColumns
    ? liveEditedColumns

        .filter((column: any) => column.visibility === ColumnVisibility.HIDDEN)

        .map((column: any) => column.fieldName)
    : [];

  const notAvailableColumnNames = liveEditedColumns
    ? liveEditedColumns
        .filter(
          (column: any) => column.visibility === ColumnVisibility.NOT_AVAILABLE,
        )

        .map((column: any) => column.fieldName)
    : [];

  return (
    <div className="flex flex-col gap-4">
      {shouldShow("workspace") && workspace && (
        <WorkspaceSection
          tableName={workspace.tableName}
          workspaces={workspace.workspaces}
          currentWorkspace={workspace.currentWorkspace}
          setCurrentWorkspace={workspace.setCurrentWorkspace}
          mutations={workspace.mutations}
          initialTableState={workspace.initialTableState}
          currentTableState={currentTableState}
          tableStateFunctions={tableStateFunctions}
          isDefault={isDefault}
          isDrawerOpen={isWorkspaceDrawerOpen}
          setIsDrawerOpen={setIsWorkspaceDrawerOpen}
          metadata={tableMetadata}
        />
      )}
      <div className="flex flex-row flex-wrap items-center justify-between gap-2">
        <div className="shrink-0">{viewSwitcher}</div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {shouldShow("workspace") && workspace && (
            <WorkspaceButton
              workspaceId={workspace.currentWorkspace?.id}
              workspaceName={workspace.currentWorkspace?.title}
              isDefault={isDefault}
              onClick={() => setIsWorkspaceDrawerOpen(true)}
            />
          )}
          {shouldShow("filters") && tableMetadata?.filters && (
            <FilterButton
              currentFilters={currentTableState.filters || {}}
              onFiltersChange={tableStateFunctions?.setFilters}
              allFiltersData={tableMetadata.filters}
              visibleColumns={visibleColumnNames}
              hiddenColumns={hiddenColumnNames}
              notAvailableColumns={notAvailableColumnNames}
              columnsTitleCreator={columnsTitleCreator}
            />
          )}
          {shouldShow("columns") &&
            (currentTableState?.screenView === "table" ||
              !currentTableState?.screenView) &&
            tableMetadata?.columns && (
              <ColumnButton
                columns={currentTableState?.columns || []}
                setColumns={tableStateFunctions?.setColumn}
                lockedColumns={currentTableState?.lockedColumns || 0}
                setLockedColumns={tableStateFunctions?.setLockedColumns}
                columnsTitleCreator={columnsTitleCreator}
                initialColumns={workspaceAsTableState?.columns || []}
                initialLockedColumns={workspaceAsTableState?.lockedColumns || 0}
                apiColumns={metadataColumnFields}
              />
            )}
          {shouldShow("plots") &&
            currentTableState?.screenView === "plots" &&
            onToggleDashboardEditor &&
            onCloseDashboardEditor &&
            dashboardEditorProps && (
              <div className="shrink-0 self-center">
                <PlotButton
                  plots={currentTableState?.plots || EMPTY_PLOTS}
                  setPlots={dashboardEditorProps.setPlots}
                  isDashboardEditorOpen={isDashboardEditorOpen}
                  onToggleDashboardEditor={onToggleDashboardEditor}
                  onCloseDashboardEditor={onCloseDashboardEditor}
                  onAddNewWidget={dashboardEditorProps.onAddNewWidget}
                  onEditPlot={dashboardEditorProps.onEditPlot}
                  onEditLayout={dashboardEditorProps.onEditLayout}
                />
              </div>
            )}
          {shouldShow("sorting") &&
            currentTableState?.screenView === "table" && (
              <SortingButton
                visibleColumns={visibleColumnNames}
                hiddenColumns={hiddenColumnNames}
                sorting={currentTableState?.sorting || []}
                setSorting={tableStateFunctions?.setSorting}
                columnsTitleCreator={columnsTitleCreator}
                initialSorting={workspaceAsTableState?.sorting}
              />
            )}
          {shouldShow("download") &&
            currentTableState?.screenView !== "plots" && (
              <DownloadButton
                name={name}
                downloadName={downloadName}
                filters={downloadFilters || currentTableState?.filters || {}}
                fixedFilters={downloadFixedFilters || {}}
                sorting={currentTableState?.sorting}
                metadata={tableMetadata}
                datasetName={datasetName}
                endpointSuffix={endpointSuffix}
                endpointPrefix={endpointPrefix}
                noFilteredDownload={noFilteredDownload}
                columns={currentTableState?.columns || []}
                columnsTitleCreator={columnsTitleCreator}
              />
            )}
          {isSuperuserMode && tableMetadata?.columns && (
            <SaveViewButton
              currentTableState={currentTableState}
              pageName={name}
              fullPageName={fullPageName}
              allColumns={metadataColumnFields ?? []}
              columnsTitleCreator={columnsTitleCreator}
              workspace={workspace}
            />
          )}
          {shouldShow("provenance") && hasProvenance && datasetName && (
            <ProvenanceButton datasetName={datasetName} title={name} />
          )}
          {shouldShow("guide") && guideLink && (
            <GuideButton pageName={guideLink} />
          )}
        </div>
      </div>
    </div>
  );
};

export default TableToolbar;
